/**
 * CardSlider - A responsive, touch-enabled card slider component
 *
 * @class
 * @description Creates a horizontal slider for card elements with touch/mouse drag support
 * and navigation buttons.
 *
 * @param {HTMLElement} container - The DOM element containing the slider structure
 *
 * @example
 * // HTML structure
 * // <div data-component="slider-cards">
 * //   <div data-slider-cards>
 * //     <div class="card">Card 1</div>
 * //     <div class="card">Card 2</div>
 * //     <div class="card">Card 3</div>
 * //   </div>
 * //   <button data-slider-prev>Previous</button>
 * //   <button data-slider-next>Next</button>
 * // </div>
 *
 * // JavaScript initialization
 * const container = document.querySelector('[data-component="slider-cards"]');
 * new CardSlider(container);
 */
class CardSlider {
  constructor(container) {
    if (!container) return;
    this.container = container;
    this.slidesWrapper = container.querySelector(':scope > div[data-slider-cards]');
    if (!this.slidesWrapper) return;

    this.slides = Array.from(this.slidesWrapper.children);
    if (!this.slides.length) return;

    this.leftBtn = container.querySelector('[data-slider-prev]');
    this.rightBtn = container.querySelector('[data-slider-next]');
    this.currentIndex = 0;
    this.startX = undefined;
    this.currentX = undefined;
    this.isDragging = false;
    this.clickableTarget = null;

    this.state = {
      containerWidth: 0,
      cardWidth: 0,
      totalCardsWidth: 0,
      maxOffset: 0
    };

    this.slidesWrapper.classList.add('tw-transition-transform', 'tw-duration-300', 'tw-will-change-transform');

    this.slides.forEach(slide => {
      slide.classList.add('tw-pointer-events-none');
      slide.querySelectorAll('a, button, [role="button"]').forEach(clickable => {
        clickable.classList.add('tw-relative', 'tw-z-30', 'tw-pointer-events-auto', 'tw-cursor-pointer', 'tw-block');
      });
    });

    this.attachEvents();
    this.updateDimensions();
    this.updateSlidePositions();
  }

  debounce(func, wait) {
    let timeout;
    return (...args) => {
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

  updateDimensions() {
    this.state.containerWidth = this.container.clientWidth;
    const computedStyle = getComputedStyle(this.slidesWrapper);
    this.state.gap = parseFloat(computedStyle.gap) || 0;

    const cardOffsetWidth = this.slides[0].offsetWidth;
    this.state.cardWidth = cardOffsetWidth + this.state.gap;

    this.state.sidePadding = (this.state.containerWidth - cardOffsetWidth) / 12;
    this.slidesWrapper.style.paddingLeft = `${this.state.sidePadding}px`;
    this.slidesWrapper.style.paddingRight = `${this.state.sidePadding}px`;

    this.state.totalCardsWidth = cardOffsetWidth * this.slides.length + this.state.gap * (this.slides.length - 1);

    this.state.maxOffset = Math.max(this.state.totalCardsWidth + this.state.sidePadding * 2 - this.state.containerWidth, 0);
  }

  updateSlidePositions() {
    this.updateDimensions();
    const translateValue = Math.min(this.currentIndex * this.state.cardWidth, this.state.maxOffset);
    this.slidesWrapper.style.transform = `translate3d(-${translateValue}px, 0, 0)`;

    if (this.leftBtn) {
      const isDisabled = translateValue === 0;
      this.leftBtn.disabled = isDisabled;
      this.leftBtn.classList.toggle('tw-opacity-40', isDisabled);
    }
    if (this.rightBtn) {
      const isDisabled = translateValue >= this.state.maxOffset;
      this.rightBtn.disabled = isDisabled;
      this.rightBtn.classList.toggle('tw-opacity-40', isDisabled);
    }
  }

  attachEvents() {
    if (this.leftBtn) this.leftBtn.addEventListener('click', () => this.showPrev());
    if (this.rightBtn) this.rightBtn.addEventListener('click', () => this.showNext());
    this.container.addEventListener('keydown', (e) => this.handleKeyDown(e));

    this.container.addEventListener('pointerdown', (e) => this.handleDragStart(e));
    this.container.addEventListener('pointermove', (e) => this.handleDragMove(e));
    this.container.addEventListener('pointerup', (e) => this.handleDragEnd(e));
    this.container.addEventListener('pointercancel', (e) => this.handleDragEnd(e));
    this.container.addEventListener('pointerleave', (e) => this.handleDragEnd(e));

    this.container.addEventListener('touchstart', (e) => this.handleDragStart(e), { passive: false });
    this.container.addEventListener('touchmove', (e) => this.handleDragMove(e), { passive: false });
    this.container.addEventListener('touchend', (e) => this.handleDragEnd());

    window.addEventListener('resize', this.debounce(() => this.updateSlidePositions(), 150));
  }

  handleKeyDown(e) {
    if (e.key === 'ArrowLeft') this.showPrev();
    if (e.key === 'ArrowRight') this.showNext();
  }

  handleDragStart(e) {
    this.clickableTarget = e.target.closest('a, button, [role="button"]');

    this.startX = (e.touches ? e.touches[0] : e).clientX;
    this.currentX = this.startX;
    this.isDragging = false;
  }

  handleDragMove(e) {
    if (this.startX === undefined) return;
    this.currentX = (e.touches ? e.touches[0] : e).clientX;

    const delta = this.currentX - this.startX;

    if (!this.isDragging) {
      if (Math.abs(delta) < 10) return;
      this.isDragging = true;
      e.preventDefault();
    }

    if (Math.abs(delta) > 50) {
      delta > 0 ? this.showPrev() : this.showNext();
      this.handleDragEnd();
    }

  }

  handleDragEnd() {
    this.isDragging = false;
    this.startX = undefined;
    this.currentX = undefined;
    this.clickableTarget = null;
  }

  showPrev() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.updateSlidePositions();
    }
  }

  showNext() {
    this.updateDimensions();
    const maxIndex = Math.ceil(this.state.totalCardsWidth / this.state.cardWidth) - 1;
    if (this.currentIndex < maxIndex) {
      this.currentIndex++;
      this.updateSlidePositions();
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-component="slider-cards"]').forEach(container => {
    new CardSlider(container);
  });
});